/**
 * Utility functions for formatting data in consistent ways throughout the application
 */

/**
 * Format a number as currency (USD)
 * @param amount Number to format as currency
 * @param options Intl.NumberFormatOptions to customize formatting
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, options: Intl.NumberFormatOptions = {}): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options
  }).format(amount);
};

/**
 * Format a date in a consistent way
 * @param date Date to format
 * @param format Format to use (short, medium, long)
 * @returns Formatted date string
 */
export const formatDate = (date: Date | number, format: 'short' | 'medium' | 'long' = 'medium'): string => {
  if (typeof date === 'number') {
    date = new Date(date);
  }
  
  switch (format) {
    case 'short':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'medium':
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    case 'long':
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', month: 'long', day: 'numeric', 
        weekday: 'long'
      });
    default:
      return date.toLocaleDateString();
  }
};

/**
 * Format a number with commas
 * @param num Number to format
 * @returns Formatted number string
 */
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

/**
 * Format a percentage
 * @param value Number to format as percentage
 * @param decimals Number of decimal places
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format location for display. Handles both string and object shapes (e.g. { address, name, coordinates }).
 * @param location Location as string or object with optional name/address
 * @returns Safe string to render in UI
 */
export const formatLocationDisplay = (
  location: string | { address?: string; name?: string; coordinates?: unknown } | null | undefined
): string => {
  if (location == null) return '';
  if (typeof location === 'string') return location;
  return String(location.name || location.address || '').trim();
};

/**
 * Truncate a string to a maximum length and add ellipsis
 * @param text Text to truncate
 * @param maxLength Maximum length before truncation
 * @returns Truncated string
 */
export const truncateText = (text: string, maxLength: number = 100): string => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Convert a timestamp or date to a relative time string (e.g., "5 minutes ago")
 * @param date Date or timestamp to format
 * @returns Relative time string
 */
export const formatRelativeTime = (date: Date | number): string => {
  const now = new Date();
  const inputDate = typeof date === 'number' ? new Date(date) : date;
  const diffInSeconds = Math.floor((now.getTime() - inputDate.getTime()) / 1000);
  
  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  } else {
    const years = Math.floor(diffInSeconds / 31536000);
    return `${years} ${years === 1 ? 'year' : 'years'} ago`;
  }
};
